// src/components/map/MapFilterBar.tsx
//
// Filter chip row for the map exploration drawer header. Sits
// just below the drag handle. Phase 1C ships two filters:
//
//   • Min rating   — tap to cycle 3.0 → 3.5 → 4.0 → 4.5 → 3.0
//   • Open now     — toggle on/off
//
// Phase 2 will add max-distance and price-level chips, plus
// persistence back to the conversation doc (so coming back to a
// layer remembers the filter state). Phase 1C is local state only
// — closing and reopening the conversation resets to the server-
// supplied defaults. That's a small UX wart; live with it for now.
//
// Why this shape: chips are the standard "quick filter" affordance
// in mobile maps (Google Maps' filter row, Apple Maps, Citymapper).
// Tap to toggle, no menus, no modals. The tradeoff is fewer
// degrees of freedom — exact rating thresholds aren't typeable.
// Acceptable; the cycle covers the meaningful range.

import React from 'react';
import { Box, Chip, Stack } from '@mui/material';
import {
  StarBorder,
  Star,
  AccessTime,
  AccessTimeFilled,
} from '@mui/icons-material';
import { useT } from '../../i18n/useT';

/** Cycle: 3.0 → 3.5 → 4.0 → 4.5 → wrap. The cloud function's
 *  default floor is 3.0, so the chip starts there for fresh
 *  conversations. We don't go below 3.0 — anything lower is
 *  noisy and the cloud function pre-filtered to ≥ that anyway.
 *  Going above 4.5 yields a near-empty list in most queries. */
const RATING_CYCLE = [3.0, 3.5, 4.0, 4.5];

function nextRating(cur: number): number {
  // Snap current to the nearest cycle entry, then advance.
  let idx = 0;
  let bestDelta = Infinity;
  for (let i = 0; i < RATING_CYCLE.length; i++) {
    const d = Math.abs(cur - RATING_CYCLE[i]);
    if (d < bestDelta) {
      bestDelta = d;
      idx = i;
    }
  }
  return RATING_CYCLE[(idx + 1) % RATING_CYCLE.length];
}

interface MapFilterBarProps {
  minRating: number;
  openNow: boolean;
  onMinRatingChange: (value: number) => void;
  onOpenNowChange: (value: boolean) => void;
  /** Disable filter taps while the layer is generating — the
   *  filters apply client-side over the server's place list, so
   *  there's no list to filter against during generation. */
  disabled?: boolean;
}

export const MapFilterBar: React.FC<MapFilterBarProps> = ({
  minRating,
  openNow,
  onMinRatingChange,
  onOpenNowChange,
  disabled = false,
}) => {
  const t = useT();

  const handleRatingTap = () => {
    if (disabled) return;
    onMinRatingChange(nextRating(minRating));
  };
  const handleOpenNowTap = () => {
    if (disabled) return;
    onOpenNowChange(!openNow);
  };

  // Format the rating as e.g. "3.0+" rather than "≥ 3.0" — fewer
  // chars, plays well in narrow phone widths, and the "+" reads
  // as "this rating or higher" without needing the math glyph.
  const ratingLabel = `${minRating.toFixed(1)}${'\u202F'}+`;

  return (
    <Box
      sx={{
        // Horizontal-scroll chip rail. With only 2 chips today
        // there's no overflow, but we set up scroll to handle the
        // Phase 2 additions (price + distance) without re-laying
        // out the parent.
        overflowX: 'auto',
        overflowY: 'hidden',
        // Hide the scrollbar — the chips are obviously a row.
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        // Generous padding so chip taps don't graze the drag
        // handle above. Match the header strip's horizontal
        // padding from the parent (px: 2).
        px: 0.25,
        py: 0.5,
        // Stop drag handlers above from claiming chip taps.
        // touchAction:'auto' wins over the parent's
        // touchAction:'none' (set on the drag handle bar).
        touchAction: 'auto',
      }}
      // The dragger above uses pointerdown bubbling to start a
      // drag. We bail in dragger code when the target is a
      // button; chips render as buttons so they're already safe.
      // But `data-no-drag` on the wrapper is a defensive belt-
      // and-suspenders for the Box children that aren't <button>.
      data-no-drag="true"
    >
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'nowrap' }}>
        {/* Min rating chip. Filled when above 3.0 (cloud function
            default) so the user can see at a glance whether they've
            tightened the floor beyond what the server returned. */}
        <Chip
          icon={
            minRating >= 4.0 ? (
              <Star sx={{ fontSize: 16 }} />
            ) : (
              <StarBorder sx={{ fontSize: 16 }} />
            )
          }
          label={ratingLabel}
          onClick={handleRatingTap}
          disabled={disabled}
          size="small"
          variant={minRating > 3.0 ? 'filled' : 'outlined'}
          color={minRating > 3.0 ? 'primary' : 'default'}
          sx={{
            fontWeight: 600,
            cursor: disabled ? 'default' : 'pointer',
            // Slightly tighter than default — chip rails feel
            // snappier with a smaller height.
            height: 28,
            // Make the icon and label hug each other.
            '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
            '& .MuiChip-label': { px: 1, fontSize: '0.78rem' },
          }}
        />

        {/* Open-now toggle. Filled when active. The clock icon
            switches filled/outlined to mirror state, same pattern
            as the rating chip. */}
        <Chip
          icon={
            openNow ? (
              <AccessTimeFilled sx={{ fontSize: 16 }} />
            ) : (
              <AccessTime sx={{ fontSize: 16 }} />
            )
          }
          label={t('Open now')}
          onClick={handleOpenNowTap}
          disabled={disabled}
          size="small"
          variant={openNow ? 'filled' : 'outlined'}
          color={openNow ? 'primary' : 'default'}
          sx={{
            fontWeight: 600,
            cursor: disabled ? 'default' : 'pointer',
            height: 28,
            '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
            '& .MuiChip-label': { px: 1, fontSize: '0.78rem' },
          }}
        />
      </Stack>
    </Box>
  );
};
